/**
 * Entity API
 */

import { apiClient } from '@shared/api/client';
import { excludeCrsUnfilableMsic } from '@/shared/constants/crsMsic';
import { useSessionStore } from '@/shared/stores/sessionStore';
import type {
    Entity,
    CreateEntityData,
    UpdateEntityData,
    EntityListParams,
    EntityListResponse,
    EntityStats,
    EntityControllerResponse,
    EntityControllerDetail,
    EntityControllerBoCriterion,
    EntityBeneficialTypeItem,
} from '../types/entity.types';

/**
 * 세션이 말레이시아인지 확인
 */
function isMalaysiaSession(): boolean {
    try {
        const sessionStore = useSessionStore();
        const customerInfo = sessionStore.user?.customer || null;
        const jurisdiction = customerInfo?.jurisdiction || '';
        return jurisdiction?.toUpperCase() === 'MY' || jurisdiction?.toUpperCase() === 'MALAYSIA';
    } catch {
        return false;
    }
}

export const entityMyApi = {
    /**
     * 엔티티 목록 조회
     */
    getEntities: (params?: EntityListParams) => {
        return apiClient.get<EntityListResponse>('/entity_my', { params });
    },

    /**
     * 엔티티 기본 정보 조회 (EntityName 섹션용)
     */
    getEntityById: (id: string) => {
        return apiClient.get<Entity>(`/entity_my/${id}`);
    },

    /**
     * 엔티티 Profile 탭 데이터 조회 (Info + Main Contact)
     */
    getEntityProfile: (id: string, opts?: { cacheBust?: boolean }) => {
        const params: Record<string, string> = {};
        if (opts?.cacheBust) params._t = String(Date.now());
        return apiClient.get<any>(`/entity_my/profile/${id}`, { params });
    },

    /**
     * 엔티티 싱크 변경 이력 조회 (회차별 Before/After) — MY 전용
     */
    getEntitySyncHistory: (entityCd: string) => {
        return apiClient.get<any>(`/entity_my/sync-history/${entityCd}`);
    },

    /**
     * 여러 엔티티의 싱크 변경 플래그 배치 조회 — 리스트 아이콘 색 결정용(비동기)
     * @returns { changes: string[], conflicts: string[] }
     *   changes=실제 변경 있음(초록), conflicts=검토 필요(빨강). 둘 다 없으면 회색.
     */
    getEntitySyncChangeFlags: (entityCds: string[]) => {
        return apiClient.post<any>('/entity_my/sync-history/change-flags', { entityCds });
    },

    /**
     * 세션 클라이언트의 "변경(change)·검토필요(conflict) 있는" entityCd 전체 목록 — Change History 필터용
     * @returns { changes: string[], conflicts: string[] }
     */
    getChangedEntitiesAll: () => {
        return apiClient.get<any>('/entity_my/sync-history/changed-entities/all');
    },

    /** L_COUNTRY_CD — 엔티티 지점 Country 콤보 (표시 Name, 값 CountryId) */
    getEntityBranchCountriesCd: () => {
        return apiClient.get<any>('/entity_my/profile/countries-cd');
    },

    /** L_ENTITY_BRANCH — 지점 신규 (MY 프로필) */
    createEntityBranch: (
        entityCd: string,
        body: {
            address?: string;
            postCode?: string;
            town?: string;
            state?: string;
            countryId?: string;
            telNo0?: string;
            telNo1?: string;
            email?: string;
            website?: string;
            faxNo0?: string;
            faxNo1?: string;
            /** 개설일 YYYY-MM-DD — 필수(OpenDate NOT NULL). AR 은 개설일 ≤ AR 기준일인 지점만 수집 */
            openDate?: string;
            /** 폐쇄일 YYYY-MM-DD — 오늘 이하면 IsClose=1 (미래 날짜는 그날까지 운영 중) */
            closeDate?: string;
        }
    ) => {
        return apiClient.post<any>(`/entity_my/profile/${entityCd}/branch`, body);
    },

    /** L_ENTITY_BRANCH — 지점 수정 */
    updateEntityBranch: (
        entityCd: string,
        branchId: string,
        body: {
            address?: string;
            postCode?: string;
            town?: string;
            state?: string;
            countryId?: string;
            telNo0?: string;
            telNo1?: string;
            email?: string;
            website?: string;
            faxNo0?: string;
            faxNo1?: string;
            /** true 이면 서버에서 IsClose=1, CloseDate=GETUTCDATE() (closeDate 를 안 보낼 때만) */
            isClose?: boolean;
            /** 보내면 OpenDate 를 이 값으로(빈 값은 거부 — 필수) */
            openDate?: string;
            /** 보내면 값 있음 = 그 날짜로 폐쇄, 빈 값 = 재개(IsClose=0, CloseDate=NULL) */
            closeDate?: string;
        }
    ) => {
        return apiClient.patch<any>(`/entity_my/profile/${entityCd}/branch/${branchId}`, body);
    },

    /** L_ENTITY_CHARGE — 담보/채무 신규 (MY 프로필) */
    createEntityCharge: (
        entityCd: string,
        body: {
            customChargeNo?: string;
            certificateDate?: string;
            dateCharge?: string;
            dateDisCharge?: string;
            hasCertificateDate?: boolean;
            hasDischargeDate?: boolean;
            rateOfInterest?: number;
            nameOfChargee?: string;
            chargeAmt?: number | null;
            chargeType?: number;
            natureOfCharge?: string;
            body?: string;
            agenda?: string;
            propertyHeld?: string;
            remark?: string;
            useCertificateDateInSection68?: boolean;
            showInReport?: boolean;
        }
    ) => {
        return apiClient.post<any>(`/entity_my/profile/${entityCd}/charge`, body);
    },

    /** L_ENTITY_CHARGE — 담보/채무 수정 */
    updateEntityCharge: (
        entityCd: string,
        chargeNo: string,
        body: {
            customChargeNo?: string;
            certificateDate?: string;
            dateCharge?: string;
            dateDisCharge?: string;
            hasCertificateDate?: boolean;
            hasDischargeDate?: boolean;
            rateOfInterest?: number;
            nameOfChargee?: string;
            chargeAmt?: number | null;
            chargeType?: number;
            natureOfCharge?: string;
            body?: string;
            agenda?: string;
            propertyHeld?: string;
            remark?: string;
            useCertificateDateInSection68?: boolean;
            showInReport?: boolean;
            isDischarge?: boolean;
        }
    ) => {
        return apiClient.patch<any>(`/entity_my/profile/${entityCd}/charge/${chargeNo}`, body);
    },

    /**
     * 엔티티 Officer 탭 데이터 조회
     * @param opts.positionGroup secretary | auditor | director | manager — POSITION_NAME LIKE 필터
     */
    getEntityOfficer: (id: string, opts?: { positionGroup?: 'secretary' | 'auditor' | 'director' | 'manager' | 'taxAgent' }) => {
        const params: Record<string, string> = {};
        if (opts?.positionGroup) params.positionGroup = opts.positionGroup;
        return apiClient.get<any>(`/entity_my/officer/${id}`, { params });
    },

    /** 직책별 인원 수 (탭 라벨용) */
    getEntityOfficerPositionCounts: (id: string) => {
        return apiClient.get<any>(`/entity_my/officer/${id}/position-counts`);
    },

    /**
     * 엔티티 Officer 포지션 목록 조회
     */
    getEntityOfficerPositions: (id: string) => {
        return apiClient.get<any>(`/entity_my/officer/${id}/positions`);
    },

    /**
     * 엔티티 Officer 상세 조회
     */
    getEntityOfficerDetail: (id: string, officerIdx: number) => {
        return apiClient.get<any>(`/entity_my/officer/${id}/detail/${officerIdx}`);
    },

    /**
     * 엔티티 Officer 수정
     */
    updateEntityOfficer: (id: string, data: {
        officerIdx: number;
        positionCode: string;
        appointmentDate: string;
    }) => {
        return apiClient.put<any>(`/entity_my/officer/${id}/detail`, data);
    },

    /**
     * L_OFFICER 직책별 역할 필드 수정 (OCCUPATION / SECRETARY_* / FIRM_NO)
     */
    updateEntityOfficerRoleFields: (
        id: string,
        data: {
            officerIdx: number | string;
            occupation?: string;
            secretaryLicenseNo?: string;
            secretaryCertificateNo?: string;
            firmNo?: string;
        },
    ) => {
        return apiClient.patch<any>(`/entity_my/officer/${id}/role-fields`, data);
    },

    /**
     * L_BENEFICIALOWNER.OCCUPATION 수정
     */
    updateEntityBeneficialOwnerRoleFields: (
        id: string,
        data: {
            ctrlIdx: number | string;
            occupation?: string;
        },
    ) => {
        return apiClient.patch<any>(`/entity_my/controller/${id}/role-fields`, data);
    },

    /**
     * 엔티티 Officer 삭제
     */
    deleteEntityOfficer: (id: string, officerIdx: number) => {
        return apiClient.delete<any>(`/entity_my/officer/${id}/detail/${officerIdx}`);
    },

    /**
     * 엔티티 Shares 탭 데이터 조회
     */
    getEntityShares: (id: string) => {
        return apiClient.get<any>(`/entity_my/shares/${id}`);
    },
    getEntityShareRegister: (id: string) => {
        return apiClient.get<any>(`/entity_my/shares/${id}/register`);
    },

    /**
     * 엔티티 Shares 탭 Shareholder 선택용 연락처 목록 조회
     */
    getEntityShareContacts: (id: string) => {
        return apiClient.get<any>(`/entity_my/shares/${id}/contacts`);
    },

    /**
     * 엔티티 Shareholder 추가
     */
    addEntityShareholder: (id: string, data: {
        shareholder: string;
        transactionDate: string;
        currency: string;
        classOfShares: string;
        numberOfShares: number;
        issuedShareCapital: number;
        paidUpShareCapital: number;
        sharesPayable?: string;
        meetingStatus?: string;
        certificateType?: string;
        certificateNo?: string;
        eventNo?: string | null;
        chairPerson?: string;
        nomineeShareholdersCode?: string | null;
        shareholderJsonData?: any;
        skipTransferorRemainder?: boolean;
        transferorRemainderDetails?: unknown[];
    }) => {
        return apiClient.post<any>(`/entity_my/shares/${id}`, data);
    },

    /** 이번 이벤트에 부여할 event_no 조회. eventType(Allot/Transfer) 지정 시 해당 종류의 최대 + 1(종류별 분리 채번). */
    getNextShareEventNo: (id: string, eventType?: string) => {
        const q = eventType ? `?eventType=${encodeURIComponent(eventType)}` : '';
        return apiClient.get<any>(`/entity_my/shares/${id}/next-event-no${q}`);
    },

    /**
     * 엔티티 Shareholder 디테일 조회
     */
    getEntityShareholderDetail: (id: string, shareCapitalIdx: number, shareholderIdx: number) => {
        return apiClient.get<any>(`/entity_my/shares/${id}/detail/${shareCapitalIdx}/${shareholderIdx}`);
    },

    /**
     * 엔티티 Shareholder 수정
     */
    updateEntityShareholder: (id: string, shareCapitalIdx: number, shareholderIdx: number, data: {
        shareholder: string;
        transactionDate: string;
        currency: string;
        classOfShares: string;
        numberOfShares: number;
        issuedShareCapital: number;
        paidUpShareCapital: number;
        sharesPayable?: string;
        meetingStatus?: string;
        certificateType?: string;
        certificateNo?: string;
        chairPerson?: string;
        nomineeShareholdersCode?: string | null;
        shareholderJsonData?: any;
    }) => {
        return apiClient.put<any>(`/entity_my/shares/${id}/${shareCapitalIdx}/${shareholderIdx}`, data);
    },

    /**
     * 엔티티 Controller 탭 데이터 조회
     */
    getEntityController: (id: string) => {
        return apiClient.get<EntityControllerResponse>(`/entity_my/controller/${id}`);
    },

    /**
     * 엔티티 Controller 상세 조회
     */
    getEntityControllerDetail: (id: string, params: { ctrlIdx: string | number; ctrlDetailIdx?: string | number; registerType?: string }) => {
        return apiClient.get<EntityControllerDetail>(`/entity_my/controller/${id}/detail`, { params });
    },

    /**
     * L_BENEFICIAL_TYPE — BO Type 콤보 옵션
     */
    getBeneficialTypes: () => {
        return apiClient.get<{ data: EntityBeneficialTypeItem[] }>(`/entity_my/controller/beneficial-types`);
    },

    /**
     * 엔티티 생성
     */
    createEntity: (data: CreateEntityData) => {
        return apiClient.post<Entity>('/entity_my/profile', data);
    },

    /**
     * 엔티티 수정
     */
    updateEntity: (id: string, data: UpdateEntityData) => {
        return apiClient.put<Entity>(`/entity_my/${id}`, data);
    },

    /**
     * 엔티티 삭제
     */
    deleteEntity: (id: string) => {
        return apiClient.delete(`/entity_my/${id}`);
    },

    /**
     * 엔티티 상태 변경
     */
    updateEntityStatus: (id: string, status: string) => {
        return apiClient.patch(`/entity_my/${id}/status`, { status });
    },

    /**
     * 엔티티 통계 조회
     */
    getEntityStats: () => {
        return apiClient.get<EntityStats>('/entity_my/stats');
    },

    /**
     * SSIC 목록 조회
     */
    getSSICList: () => {
        return apiClient.get<any>('/entity_my/profile/ssic/list');
    },

    /**
     * Entity Type 목록 조회 (M03, SG)
     */
    getEntityTypes: () => {
        return apiClient.get<any>('/entity_my/profile/entity-types');
    },

    /**
     * Login Users 목록 조회 (Account Manager용)
     */
    getUsersList: () => {
        return apiClient.get<any>('/entity_my/profile/users/list');
    },

    /**
     * Global Contact 목록 조회 (Main Contact 선택용)
     */
    getContactsList: () => {
        return apiClient.get<any>('/entity_my/profile/contacts/list');
    },

    /**
     * Malaysia State 목록 조회
     */
    getMalaysiaStates: () => {
        return apiClient.get<{ data: Array<{ code: string; name: string }> }>('/entity_my/location/states');
    },

    /**
     * Malaysia City 목록 조회
     */
    getMalaysiaCities: (stateCode?: string, cityCode?: string, getAll?: boolean) => {
        const params: any = {};
        if (stateCode) params.state = stateCode;
        if (cityCode) params.cityCode = cityCode;
        if (getAll) params.all = 'true';
        return apiClient.get<{ data: Array<{ code: string; name: string }> }>('/entity_my/location/cities', {
            params
        });
    },

    /**
     * Malaysia Postcode 목록 조회
     */
    getMalaysiaPostcodes: (cityCode: string) => {
        return apiClient.get<{ data: Array<{ code: string; name: string }> }>('/entity_my/location/postcodes', {
            params: { city: cityCode }
        });
    },

    /**
     * 엑셀 업로드 기반 엔티티 날짜/주소 일괄 저장 (REG_NO 기준 UPDATE)
     * TEMP: 백엔드에서 비활성 — 일괄 등록(INSERT: bulkRequestApproval)만 사용
     */
    bulkExcelUpdate: (rows: any[]) => {
        return apiClient.post<any>('/entity_my/bulk-excel-update', { rows });
    },

    /**
     * 우편번호 숫자로 Postcode 정보 조회 (State, City 포함)
     */
    getMalaysiaPostcodesByNumber: (number: string) => {
        return apiClient.get<{ data: Array<{ code: string; name: string; state: { code: string; name: string | null }; city: { code: string; name: string } | null }> }>('/entity_my/location/postcodes/by-number', {
            params: { number }
        });
    },

    /**
     * 모든 Malaysia Postcode 목록 조회
     */
    getAllMalaysiaPostcodes: () => {
        return apiClient.get<{ data: Array<{ code: string; name: string }> }>('/entity_my/location/postcodes/all');
    },

    /**
     * Malaysia Business Code (MSIC) 목록 조회
     *
     * `crsFilableOnly` 를 주면 CRS 가 받지 않는 업종을 빼고 돌려준다 — 설립 신고로 이어지는
     * 화면(T-Link · DI)에서 쓴다. 고르고 나서 제출 단계에서 막히면 늦다(사유는 crsMsic.ts).
     */
    getMalaysiaBusinessCodes: async (options?: { crsFilableOnly?: boolean }) => {
        const res = await apiClient.get<{ data: Array<{ code: string; name: string }> }>('/entity_my/location/business-codes');
        if (!options?.crsFilableOnly) return res;
        // 응답을 제자리에서 고치지 않는다 — 나중에 캐시가 붙으면 필터 안 쓰는 화면까지 오염된다.
        //   백엔드는 { data: [...] } 로 감싸 주지만 호출부마다 풀어 쓰는 모양이 달라 둘 다 받는다.
        const holder: any = (res as any)?.data;
        if (Array.isArray(holder?.data)) {
            return { ...(res as any), data: { ...holder, data: excludeCrsUnfilableMsic(holder.data) } };
        }
        if (Array.isArray(holder)) {
            return { ...(res as any), data: excludeCrsUnfilableMsic(holder) };
        }
        return res;
    },

    /**
     * 엔티티에 Contact 추가 (InsertRecord 규칙 적용)
     */
    entityContactInsertRecord: (entityCd: string, contactCd: string) => {
        return apiClient.post<any>(`/entity_my/profile/${entityCd}/contact`, {
            contactCd: contactCd
        });
    },

    /**
     * 엔티티에서 Contact 삭제 (DeleteRecord 규칙 적용)
     */
    entityContactDeleteRecord: (entityCd: string, contactCd: string) => {
        return apiClient.delete<any>(`/entity_my/profile/${entityCd}/contact/${contactCd}`);
    },

    /**
     * Postal Code로 주소 검색
     */
    searchPostalCode: (postalCode: string) => {
        return apiClient.get<{
            blockHouseNo?: string;
            streetName: string;
            buildingEstateName?: string;
        }>('/entity_my/profile/search-postal-code', {
            params: { postalCode },
        });
    },

    /**
     * 엔티티를 비활성화 (IS_ACTIVE = '0')
     */
    setEntityInactive: (id: string) => {
        return apiClient.patch<any>(`/entity_my/profile/${id}/inactive`);
    },

    /**
     * 엔티티를 활성화 (IS_ACTIVE = '1')
     */
    setEntityActive: (id: string) => {
        return apiClient.patch<any>(`/entity_my/profile/${id}/active`);
    },

    /**
     * 엔티티 업데이트 (UpdateRecord 규칙 적용)
     */
    entityUpdateRecord: (id: string, data: any) => {
        console.log('📤 [entityMyApi.entityUpdateRecord] API 호출:', {
            id,
            hasAlternateAddress: !!data.alternateAddress,
            alternateAddress: data.alternateAddress,
            dataKeys: Object.keys(data),
            fullData: JSON.parse(JSON.stringify(data))
        });
        return apiClient.patch<any>(`/entity_my/profile/${id}`, data);
    },

    /**
     * Registration Number 중복 체크
     * @param regNo - 확인할 Registration Number
     * @param isValidation - Save 시 validation 체크인지 여부 (true면 VALID001 반환, false면 REG001 반환)
     */
    checkRegistrationNumber: (regNo: string, isValidation: boolean = false, entityCd?: string) => {
        const params: any = { 
            regNo,
            validation: isValidation ? 'true' : undefined
        };
        if (entityCd) {
            params.entityCd = entityCd;
        }
        return apiClient.get<any>('/entity_my/profile/check-registration-number', {
            params
        });
    },

    /**
     * Entity Create 시 validation 체크
     * @param checkType - 체크 타입 ('duplicate-check' 등)
     */
    validateEntityCreate: (checkType: string = 'duplicate-check') => {
        return apiClient.get<any>('/entity_my/profile/validate-entity-create', {
            params: { checkType }
        });
    },

    /**
     * 엔티티 Officer 수동 추가
     */
    addEntityOfficer: (id: string, data: { positionCode: string; contactCd?: string; appointmentDate: string; contactRegNo?: string; contactName?: string; occupation?: string }) => {
        return apiClient.post<any>(`/entity_my/officer/${id}`, data);
    },

    /**
     * 엔티티 Officer 연락처 옵션 조회
     */
    getEntityOfficerContacts: (id: string) => {
        return apiClient.get<any>(`/entity_my/officer/${id}/contacts`);
    },

    /**
     * 엔티티 Controller 옵션 조회 (B09, B07 공통코드)
     */
    getEntityControllerOptions: (id: string) => {
        return apiClient.get<any>(`/entity_my/controller/${id}/options`);
    },

    /**
     * 엔티티 Controller 추가
     */
    addEntityController: (id: string, data: {
        fullName: string;
        contactCd?: string;
        dateOfAppointed?: string;
        dateOfCessation?: string;
        dateOfEntry?: string;
        dateOfNoticeSent?: string;
        dateOfConfirmation?: string;
        cessationReason?: string;
        confirmationController?: string;
        requireControllerSignature?: string;
        categoryCode: string;
        representativeCd?: string;
        representBy?: string;
        acrAuen?: string;
        nonAcrAuen?: string;
        legalForm?: string;
        jurisdictionScraping?: string;
        jurisdiction?: string;
        incorporationStatute?: string;
        corporateEntityRegister?: string;
        nationalityScraping?: string;
        nationalityCd?: string;
        identificationNo?: string;
        aliases?: string;
        dateOfBirth?: string;
        addressTypeCd: string;
        postalCode?: string;
        blockNo?: string;
        streetName?: string;
        buildingName?: string;
        addrLevel?: string;
        addrUnit?: string;
        foreignAddressLine1?: string;
        foreignAddressLine2?: string;
        contactEmail?: string;
        countryCd?: string;
        areaCode?: string;
        mobileNo?: string;
        remarks?: string;
        residentialAddressChanged?: boolean;
        residentialAddressData?: Record<string, string>;
        boTypeId?: number | null;
        isJointArrangement?: boolean;
        isSeniorManagement?: boolean;
        smReasonId?: number | null;
        smEnactmentAct?: string | null;
        boCriteria?: Array<{
            code: string;
            description?: string;
            selected?: boolean;
            percentName?: string;
            percentNameSecondary?: string;
            percent?: number | string | null;
            percentSecondary?: number | string | null;
        }>;
    }) => {
        return apiClient.post<any>(`/entity_my/controller/${id}`, data);
    },

    /**
     * 엔티티 Controller 수정
     */
    updateEntityController: (id: string, data: {
        ctrlIdx: number;
        fullName: string;
        dateOfAppointed?: string;
        dateOfCessation?: string;
        dateOfEntry?: string;
        dateOfNoticeSent?: string;
        dateOfConfirmation?: string;
        cessationReason?: string;
        confirmationController?: string;
        requireControllerSignature?: string;
        categoryCode: string;
        representativeCd?: string;
        representBy?: string;
        acrAuen?: string;
        nonAcrAuen?: string;
        legalForm?: string;
        jurisdictionScraping?: string;
        jurisdiction?: string;
        incorporationStatute?: string;
        corporateEntityRegister?: string;
        nationalityScraping?: string;
        nationalityCd?: string;
        identificationNo?: string;
        aliases?: string;
        dateOfBirth?: string;
        addressTypeCd: string;
        postalCode?: string;
        blockNo?: string;
        streetName?: string;
        buildingName?: string;
        addrLevel?: string;
        addrUnit?: string;
        foreignAddressLine1?: string;
        foreignAddressLine2?: string;
        contactEmail?: string;
        countryCd?: string;
        areaCode?: string;
        mobileNo?: string;
        remarks?: string;
        boTypeId?: number | null;
        isJointArrangement?: boolean;
        isSeniorManagement?: boolean;
        smReasonId?: number | null;
        smEnactmentAct?: string | null;
        isDirector?: boolean;
        isMember?: boolean;
        boCriteria?: EntityControllerBoCriterion[];
        occupation?: string;
        contactProfileRace?: string;
    }) => {
        return apiClient.put<any>(`/entity_my/controller/${id}/detail`, data);
    },

    /**
     * 엔티티 Controller 삭제
     */
    deleteEntityController: (id: string, ctrlIdx?: string, ctrlDetailIdx?: string) => {
        const params = new URLSearchParams();
        if (ctrlIdx) params.append('ctrlIdx', ctrlIdx);
        if (ctrlDetailIdx) params.append('ctrlDetailIdx', ctrlDetailIdx);
        const queryString = params.toString();
        return apiClient.delete<any>(`/entity_my/controller/${id}/detail${queryString ? `?${queryString}` : ''}`);
    },

    /**
     * 엔티티 Shareholder 삭제
     */
    deleteEntityShareholder: (id: string, shareCapitalIdx: number, shareholderIdx: number) => {
        return apiClient.delete<any>(`/entity_my/shares/${id}/${shareCapitalIdx}/${shareholderIdx}`);
    },

    /**
     * 클라이언트 정보 조회 (CLIENT_CD로 L_CLIENTS 테이블 조회)
     * @param clientCd - 클라이언트 코드 (세션의 customer.code)
     */
    getClientInfo: (clientCd: string) => {
        return apiClient.get<{
            CLIENT_NM: string;
            REG_NO: string;
        }>(`/entity_my/client-info/${clientCd}`);
    },

    /**
     * 동기화 상태 확인 (L_DAEMON_SYNC_BIZFILE_QUEUE 테이블에서 CLIENT_CD로 status 조회)
     * CLIENT_CD는 세션에서 자동으로 가져옴
     */
    checkSyncStatus: () => {
        return apiClient.get<{
            hasActiveSync: boolean;
            status: number;
        }>(`/entity_my/sync-status`);
    },

    /**
     * Officer를 레지스터에 등록
     */
    registerOfficer: (entityCd: string, data: {
        contactCd: string;
        positionCode: string;
        appointmentDate: string;
        registerType?: string;
        directorCd?: string; // Alternate Director의 경우 Principal Director의 contactCd
    }) => {
        // positionCode에 따라 적절한 레지스터 엔드포인트로 라우팅
        // 주의: 더 구체적인 조건을 먼저 체크해야 함 (예: NOMINEE DIRECTOR는 DIRECTOR보다 먼저)
        const positionName = data.positionCode?.trim().toUpperCase() || '';
        
        if (positionName.includes('NOMINEE DIRECTOR')) {
            // Nominee Director인 경우 (DIRECTOR보다 먼저 체크)
            return apiClient.post<any>('/registerfromcontact/nomineeDirector', {
                entityCd,
                contactCd: data.contactCd,
                appointmentDate: data.appointmentDate,
                registerType: data.registerType
            });
        }
        
        if (positionName.includes('ALTERNATE DIRECTOR') || (data as any).directorCd || (data as any)?.alternateDirector?.directorCd) {
            // Alternate Director인 경우 (DIRECTOR보다 먼저 체크)
            const directorCd = ((data as any).directorCd || (data as any)?.alternateDirector?.directorCd || '').toString().trim();
            if (!directorCd) {
                return Promise.reject(new Error('alternateDirector.directorCd is required'));
            }
            return apiClient.post<any>('/registerfromcontact/alternate-director', {
                entityCd,
                contactCd: data.contactCd,
                appointmentDate: data.appointmentDate,
                registerType: data.registerType,
                alternateDirector: {
                    directorCd
                }
            });
        }
        
        if (positionName.includes('DIRECTOR')) {
            // Director인 경우
            // 말레이시아 세션일 때는 /my/director 엔드포인트 사용
            const endpoint = isMalaysiaSession() ? '/registerfromcontact/my/director' : '/registerfromcontact/director';
            return apiClient.post<any>(endpoint, {
                entityCd,
                contactCd: data.contactCd,
                appointmentDate: data.appointmentDate,
                registerType: data.registerType
            });
        }
        
        if (positionName.includes('OWNER')) {
            // Owner인 경우
            return apiClient.post<any>('/registerfromcontact/owner', {
                entityCd,
                contactCd: data.contactCd,
                appointmentDate: data.appointmentDate,
                registerType: data.registerType
            });
        }
        
        if (positionName.includes('SECRETARY')) {
            // Secretary인 경우
            return apiClient.post<any>('/registerfromcontact/secretary', {
                entityCd,
                contactCd: data.contactCd,
                appointmentDate: data.appointmentDate,
                registerType: data.registerType
            });
        }
        
        if (positionName.includes('CHIEF EXECUTIVE OFFICER') || positionName.includes('CEO')) {
            // CEO인 경우
            return apiClient.post<any>('/registerfromcontact/ceo', {
                entityCd,
                contactCd: data.contactCd,
                appointmentDate: data.appointmentDate,
                registerType: data.registerType
            });
        }
        
        if (positionName.includes('AUDITOR')) {
            // Auditor인 경우
            return apiClient.post<any>('/registerfromcontact/auditor', {
                entityCd,
                contactCd: data.contactCd,
                appointmentDate: data.appointmentDate,
                registerType: data.registerType
            });
        }
        
        if (positionName.includes('DATA PROTECTION OFFICER') || positionName.includes('DPO') || positionName.includes('DATA PROTECTION')) {
            // Data Protection Officer인 경우
            return apiClient.post<any>('/registerfromcontact/dpo', {
                entityCd,
                contactCd: data.contactCd,
                appointmentDate: data.appointmentDate,
                registerType: data.registerType
            });
        }
        
        // 기타 레지스터 타입은 나중에 추가
        return Promise.reject(new Error(`Unsupported register type: ${data.positionCode}`));
    },

    /**
     * Address를 레지스터에 등록
     */
    registerAddress: (entityCd: string, data: {
        addressData: {
            addressTypeCd?: string;
            postalCode?: string;
            blockNo?: string;
            streetName?: string;
            buildingName?: string;
            addrLevel?: string;
            addrUnit?: string;
            foreignAddressLine1?: string;
            foreignAddressLine2?: string;
        };
        dateOfEffective?: string;
        note?: string;
        contactIdUen?: string;
        contactName?: string;
    }) => {
        // 말레이시아 세션일 때는 /my/address 엔드포인트 사용
        const endpoint = isMalaysiaSession() ? '/registerfromcontact/my/address' : '/registerfromcontact/address';
        return apiClient.post<any>(endpoint, {
            entityCd,
            addressData: data.addressData,
            dateOfEffective: data.dateOfEffective,
            note: data.note,
            contactIdUen: data.contactIdUen || '',
            contactName: data.contactName || ''
        });
    },

    /**
     * Allotment를 레지스터에 등록
     */
    registerAllotment: (entityCd: string, data: {
        contactCd: string;
        transactionDate: string;
        certificateNo?: string;
        certificateType?: string;
        classOfShares: string;
        currency: string;
        subClass?: string;
        numberOfShares?: number;
        issuedShareCapital?: number;
        paidUpShareCapital?: number;
        registerType?: string;
        nomineeShareholdersCode?: string | null;
    }) => {
        // 말레이시아 세션일 때는 /my/allotment 엔드포인트 사용
        const endpoint = isMalaysiaSession() ? '/registerfromcontact/my/allotment' : '/registerfromcontact/allotment';
        return apiClient.post<any>(endpoint, {
            entityCd,
            contactCd: data.contactCd,
            transactionDate: data.transactionDate,
            certificateNo: data.certificateNo,
            certificateType: data.certificateType,
            classOfShares: data.classOfShares,
            currency: data.currency,
            subClass: data.subClass,
            numberOfShares: data.numberOfShares,
            issuedShareCapital: data.issuedShareCapital,
            paidUpShareCapital: data.paidUpShareCapital,
            registerType: data.registerType,
            nomineeShareholdersCode: data.nomineeShareholdersCode
        });
    },

    /**
     * Controller 레지스터 등록
     */
    registerController: (entityCd: string, data: {
        contactCd: string;
        dateOfEntry?: string;
        dateOfAppointed?: string;
        dateOfCessation?: string;
        cessationReason?: string;
        aliases?: string;
        legalForm?: string;
        jurisdiction?: string;
        incorporationStatute?: string;
        corporateEntityRegister?: string;
        nonAcrAuen?: string;
        dateOfConfirmation?: string;
        categoryCode?: string;
        registerType?: string;
    }) => {
        // 말레이시아 세션일 때는 /my/controller 엔드포인트 사용
        const endpoint = isMalaysiaSession() ? '/registerfromcontact/my/controller' : '/registerfromcontact/controller';
        return apiClient.post<any>(endpoint, {
            entityCd,
            contactCd: data.contactCd,
            dateOfEntry: data.dateOfEntry,
            dateOfAppointed: data.dateOfAppointed,
            dateOfCessation: data.dateOfCessation,
            cessationReason: data.cessationReason,
            aliases: data.aliases,
            legalForm: data.legalForm,
            jurisdiction: data.jurisdiction,
            incorporationStatute: data.incorporationStatute,
            corporateEntityRegister: data.corporateEntityRegister,
            nonAcrAuen: data.nonAcrAuen,
            dateOfConfirmation: data.dateOfConfirmation,
            categoryCode: data.categoryCode,
            registerType: data.registerType
        });
    },

    /**
     * Registry Sync - 일괄 등록번호 업로드
     */
    registrySync: (
        companies: Array<{
            regNo: string;
            newRegNo?: string;
            oldRegNo?: string;
            companyName?: string;
            companyType?: string;
            dateOfIncorporation?: string;
            financialYearEnd?: string;
            agmDuedate?: string;
            arDuedate?: string;
            registeredAddress?: Record<string, unknown>;
            serviceAddress?: Record<string, unknown>;
            financialRecordsKeptAddress?: Record<string, unknown>;
            membersKeptAddress?: Record<string, unknown>;
        }>
    ) => {
        return apiClient.post<any>('/entity_my/registry-sync', { companies });
    },

    /**
     * 개별 싱크 후 L_USER_ENTITY와 L_ENTITY를 ENTITY_CD 기준으로 매핑
     * - REG_NO로 L_ENTITY에서 ENTITY_CD를 찾고 L_USER_ENTITY.ENTITY_CD를 업데이트
     */
    bindEntityUser: (regNo: string) => {
        return apiClient.post<any>('/entity_my/bind-entity-user', { regNo });
    },

    /**
     * Malaysia SSM 로그인
     */
    malaysiaSsmLogin: (data: {
        credential_id?: string;
        user_id?: string;
        user_password?: string;
        login_type?: string;
        mst_idx?: number;
        registration_number?: string;
        entity_name?: string;
    }) => {
        const headers: any = {};
        if (data.mst_idx !== undefined && data.mst_idx !== null) {
            headers['x-mst-idx'] = String(data.mst_idx);
            console.log('[entityApi] malaysiaSsmLogin - 헤더에 MST_IDX 추가:', {
                mst_idx: data.mst_idx,
                header_value: headers['x-mst-idx']
            });
        } else {
            console.log('[entityApi] malaysiaSsmLogin - MST_IDX 없음, 헤더 추가 안함:', {
                mst_idx: data.mst_idx,
                isUndefined: data.mst_idx === undefined,
                isNull: data.mst_idx === null
            });
        }
        const { mst_idx, ...body } = data;
        console.log('[entityApi] malaysiaSsmLogin - 요청 전송:', {
            url: '/marketplace/v1/malaysia/ssm-session/login',
            body: body,
            headers: headers,
            has_mst_idx_header: !!headers['x-mst-idx']
        });
        return apiClient.post<any>('/marketplace/v1/malaysia/ssm-session/login', body, { headers });
    },

    /**
     * Malaysia Entity Info 조회
     * - suppressFailureEmail: true 이면 백엔드에서 개별 실패 메일을 보내지 않도록 헤더로 전달
     */
    malaysiaEntityInfo: (params: {
        session_id?: string;
        credential_id?: string;
        company_no: string;
        mst_idx?: number;
        suppressFailureEmail?: boolean;
    }) => {
        const headers: any = {};
        if (params.mst_idx) {
            headers['x-mst-idx'] = String(params.mst_idx);
        }
        if (params.suppressFailureEmail) {
            headers['x-suppress-entity-info-email'] = 'true';
        }
        const { mst_idx, suppressFailureEmail, ...queryParams } = params;
        return apiClient.get<any>('/marketplace/v1/malaysia/entity/info', { 
            params: queryParams,
            headers 
        });
    },

    /**
     * Malaysia CRS 회사정보 경량 조회 (Registry Sync 미리보기용)
     * - MYCOID가 아닌 CRS getEntityInfo로 회사명/유형/신번호/등기주소만 조회
     */
    malaysiaCrsEntityInfo: (data: {
        company_no: string;
        crs_credential_id?: string;
        crs_token?: string;
    }) => {
        return apiClient.post<{
            success: boolean;
            company_no?: string;
            company_info?: {
                company_full_name?: string;
                company_name?: string;
                company_type?: string;
                company_no_new?: string;
                registered_address?: {
                    street01?: string; street02?: string; street03?: string;
                    city?: string; state?: string; post_code?: string; country?: string;
                } | null;
            } | null;
            error?: string;
        }>('/marketplace/v1/malaysia/entity/crs-info', data);
    },

    /**
     * Malaysia Search Registration No - 구번호로 신번호 조회
     */
    malaysiaSearchRegNo: (params: {
        session_id?: string;
        credential_id?: string;
        old_reg_no: string;
        mst_idx?: number;
    }) => {
        const headers: any = {};
        if (params.mst_idx) {
            headers['x-mst-idx'] = String(params.mst_idx);
        }
        const { mst_idx, ...queryParams } = params;
        return apiClient.get<any>('/marketplace/v1/malaysia/entity/search-reg-no', {
            params: queryParams,
            headers
        });
    },

    /**
     * Malaysia Entity Info 호출 실패 종합 메일 발송
     * - 풀싱크 모달의 엔티티 정보 로드 단계에서 실패한 REG_NO 목록을 한 번에 전달
     */
    sendEntityInfoFailuresSummary: (data: {
        mstIdx: number | null;
        failures: Array<{ regNo: string; errorMessage: string }>;
        startTimeMs?: number;
        endTimeMs?: number;
    }) => {
        return apiClient.post<any>('/entity_my/entity-info-failures-summary', data);
    },

    /**
     * Malaysia Full Sync 실행 (여러 회사 일괄 조회)
     */
    malaysiaFullSyncExecute: (data: {
        registration_numbers: string[];
        credential_id?: string;
        mycoid_credential_id?: string;
        idpro_credential_id?: string;
        crs_credential_id?: string;
        crs_token?: string;
        mycoid2016_session_id?: string;
        mycoid2016?: { user_id: string; user_password: string };
        idpro_session_id?: string;
        idpro?: { user_id: string; user_password: string };
        mst_idx?: number;
        companies?: Array<{ registration_number: string; entity_name?: string }>;
    }) => {
        const headers: any = {};
        if (data.mst_idx) {
            headers['x-mst-idx'] = String(data.mst_idx);
        }
        const { mst_idx, ...body } = data;
        // Phase 1(즉시응답 전)에 CRS 재로그인이 동기로 포함된다 — 프록시(iProyal) 경유 시 로그인만 13초+,
        // 여기에 MST 저장/시작 이메일 발송까지 더해지면 30초를 넘겨 ack를 못 받고 "Full Sync Failed"가 떴다.
        // 실제 수집은 백엔드가 setImmediate로 계속 돌기 때문에, 넉넉히 기다렸다가 ack만 받으면 된다.
        return apiClient.post<any>('/marketplace/v1/malaysia/full-sync/execute', body, { headers, timeout: 180000 });
    },

    /**
     * Malaysia Full Sync 진행 상태 조회 (mst_idx 기준)
     */
    malaysiaFullSyncProgress: (mstIdx: number) => {
        return apiClient.get<any>(`/marketplace/v1/malaysia/full-sync/progress/${mstIdx}`);
    },

    /**
     * Malaysia Full Sync 진행 여부(현재 클라이언트 기준)
     */
    malaysiaFullSyncActive: () => {
        return apiClient.get<any>(`/marketplace/v1/malaysia/full-sync/active`);
    },

    /**
     * Malaysia Full Sync MST 생성
     */
    malaysiaFullSyncCreateMst: (data: {
        registration_number: string;
        entity_name?: string;
    }) => {
        return apiClient.post<any>('/marketplace/v1/malaysia/full-sync/create-mst', data);
    },

    /**
     * Malaysia Full Sync Single 실행 (단일 회사 개별 조회)
     */
    malaysiaFullSyncSingle: (data: {
        registration_number: string;
        credential_id?: string;
        mycoid_credential_id?: string;
        idpro_credential_id?: string;
        crs_credential_id?: string;
        crs_token?: string;
        mycoid2016_session_id?: string;
        mycoid2016?: { user_id: string; user_password: string };
        idpro_session_id?: string;
        idpro?: { user_id: string; user_password: string };
        mst_idx?: number;
    }) => {
        const headers: any = {};
        if (data.mst_idx) {
            headers['x-mst-idx'] = String(data.mst_idx);
        }
        const { mst_idx, ...body } = data;
        return apiClient.post<any>('/marketplace/v1/malaysia/full-sync/single', body, { headers });
    },

    /**
     * Contact Profile 조회 (주소 정보 포함)
     */
    getContactProfile: (contactCd: string) => {
        return apiClient.get<any>(`/entity_my/controller/contact-profile/${contactCd}`);
    },

    /**
     * REG_NO 배열로 승인 상태 확인 (L_USER_ENTITY 조회)
     */
    checkApprovalStatus: (regNos: string[]) => {
        return apiClient.post<{
            data: Array<{
                regNo: string;
                companyName: string | null;
                companyType: string | null;
                hasApproval: boolean;
                statusCd?: string | null;
            }>;
        }>('/entity_my/check-approval-status', { regNos });
    },

    /**
     * Request Approval - L_USER_ENTITY에 INSERT
     */
    requestApproval: (data: {
        regNo: string;
        companyName: string | null;
        companyType: string | null;
    }) => {
        return apiClient.post<{
            success: boolean;
            ueId: number;
            message: string;
        }>('/entity_my/request-approval', data);
    },

    /** 일괄 등록 전 REG_NO 서버 검증 (엑셀 COMPANY_NAME/COMPANY_TYPE 포함 가능) */
    bulkValidateRegNos: (
        regNosOrItems: string[] | Array<{ regNo: string; companyName?: string | null; companyType?: string | null }>,
    ) => {
        const body = Array.isArray(regNosOrItems) && typeof regNosOrItems[0] === 'string'
            ? { regNos: regNosOrItems as string[] }
            : { items: regNosOrItems as Array<{ regNo: string; companyName?: string | null; companyType?: string | null }> };
        return apiClient.post<{
            items: Array<{
                inputRegNo: string;
                canonicalRegNo: string | null;
                companyName: string | null;
                companyType: string | null;
                status: 'ready' | 'ready_new' | 'already_registered' | 'not_found';
                message: string | null;
            }>;
            summary: {
                total: number;
                ready: number;
                ready_existing?: number;
                ready_new?: number;
                already_registered: number;
                not_found: number;
            };
            clientCd?: string;
        }>('/entity_my/bulk-validate-regnos', body);
    },

    /** L_USER_ENTITY 일괄 등록 (승인 요청 또는 즉시 배정) + 날짜/주소 / INSERT only */
    bulkRequestApproval: (
        items: Array<{
            regNo: string;
            regNoOld?: string | null;
            companyName?: string | null;
            companyType?: string | null;
            dateOfIncorporation?: string | null;
            financialYearEnd?: string | null;
            agmDuedate?: string | null;
            arDuedate?: string | null;
            nextAccDuedate?: string | null;
            registeredAddress?: Record<string, any> | null;
            serviceAddress?: Record<string, any> | null;
            financialRecordsKeptAddress?: Record<string, any> | null;
            membersKeptAddress?: Record<string, any> | null;
        }>,
        options?: { insertOnly?: boolean },
    ) => {
        return apiClient.post<{
            success: boolean;
            total: number;
            succeeded: number;
            skipped: number;
            failed: number;
            createdEntities?: number;
            mode?: 'direct' | 'approval';
            authEnabled?: boolean;
            isManagerUser?: boolean;
            results: Array<{
                regNo: string;
                status: 'success' | 'skipped' | 'failed';
                ueId?: number;
                statusCd?: string | null;
                mode?: 'direct' | 'approval';
                entityCreated?: boolean;
                entityCd?: string | null;
                error?: string;
            }>;
        }>('/entity_my/bulk-request-approval', {
            items,
            insertOnly: options?.insertOnly === true,
        });
    },

    /**
     * Malaysia SSM 로그아웃
     */
    malaysiaSsmLogout: (data: {
        session_id?: string;
        credential_id?: string;
        login_type?: string;
        mst_idx?: number;
    }) => {
        const headers: any = {};
        if (data.mst_idx) {
            headers['x-mst-idx'] = String(data.mst_idx);
        }
        const { mst_idx, ...body } = data;
        return apiClient.post<any>('/marketplace/v1/malaysia/ssm-session/logout', body, { headers });
    },
};

